/**
 * Sidebar — Plattform-Shell (UX_KONZEPT §1.2/§6.5), portiert aus
 * `rmnc-personal/frontend/src/components/layout/Sidebar.tsx`
 * (docs/SHELL_PORTIERUNG.md). App-spezifisch angepasst: `navItems` für
 * Stammdaten, Rollen-Quelle `useAuth()` (statt `useAuthStore`), Branding-Kopf
 * als lokale Konstante (kein `src/branding`-Theming-System hier), Akzent
 * violett (Stammdaten-Farbe #9b59b6, AppSwitcher-Kachel-Akzent).
 */
import { Link, useLocation } from "react-router-dom"
import {
  Building2,
  Users,
  Package,
  Truck,
  ShieldCheck,
  Wallet,
  Settings,
  Wrench,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/auth/AuthProvider"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

/** Blatt-Eintrag: navigiert direkt zu einer Route. */
export interface NavLeaf {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Nur sichtbar, wenn der Benutzer mindestens eine dieser Rollen hat.
   *  Frontend-Rollen sind reines Ein-/Ausblenden — durchgesetzt wird serverseitig. */
  requiredRoles?: string[]
}

export type NavEntry = NavLeaf

/**
 * Statischer Navigations-Index der App (vorher Kopfleisten-Nav in App.tsx).
 * Finanzen: `app:stammdaten:finanzen` oder `admin`. Einstellungen: `admin`.
 */
export const navItems: NavEntry[] = [
  { title: "Organisationen", href: "/organisationen", icon: Building2 },
  { title: "Kontakte", href: "/kontakte", icon: Users },
  { title: "Artikel", href: "/artikel", icon: Package },
  { title: "Lieferbedingungen", href: "/lieferbedingungen", icon: Truck },
  { title: "Zertifikate", href: "/zertifikate", icon: ShieldCheck },
  {
    title: "Finanzen",
    href: "/finanzen",
    icon: Wallet,
    requiredRoles: ["app:stammdaten:finanzen", "admin"],
  },
  { title: "Pflege", href: "/pflege", icon: Wrench },
  { title: "Einstellungen", href: "/einstellungen", icon: Settings, requiredRoles: ["admin"] },
]

/** Sichtbarkeits-Filter: Eintrag ohne requiredRoles ist immer sichtbar. */
export function filterNavByRoles(
  entries: NavEntry[],
  roles: readonly string[] | undefined | null
): NavEntry[] {
  const allowed = (required?: string[]) =>
    !required || required.length === 0 || (roles ?? []).some((r) => required.includes(r))
  return entries.filter((entry) => allowed(entry.requiredRoles))
}

interface SidebarProps {
  collapsed?: boolean
  /**
   * App-Akzentfarbe als HSL-Triplet. Überschreibt `--primary` lokal für die
   * Sidebar (Aktiv-Marker, Monogramm). Stammdaten: "283 39% 53%" (#9b59b6).
   */
  accent?: string
}

export function Sidebar({ collapsed = false, accent }: SidebarProps) {
  const location = useLocation()
  const { user } = useAuth()
  const roles = user?.roles

  const visibleItems = filterNavByRoles(navItems, roles)

  function isActive(href: string): boolean {
    return location.pathname === href || location.pathname.startsWith(href + "/")
  }

  function leafClasses(active: boolean): string {
    return cn(
      "flex items-center gap-3 border-l-4 border-transparent px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900",
      active && "sidebar-item-active",
      collapsed && "justify-center px-0"
    )
  }

  function renderLeaf(item: NavLeaf) {
    const Icon = item.icon
    const link = (
      <Link key={item.href} to={item.href} className={leafClasses(isActive(item.href))}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.title}</span>}
      </Link>
    )
    if (!collapsed) return link
    return (
      <Tooltip key={item.href}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.title}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <aside
      style={accent ? ({ "--primary": accent } as React.CSSProperties) : undefined}
      className={cn(
        "flex h-full flex-col border-r border-slate-200 bg-white text-slate-900 transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* FabrikOS-Plattformmarke: ausgeklappt Wortmarke, eingeklappt Symbol */}
      <div className={cn("flex h-14 shrink-0 items-center border-b border-slate-200 px-4", collapsed && "justify-center px-0")}>
        <Link to="/organisationen" aria-label="FabrikOS">
          {collapsed ? (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/symbol.jpg`} alt="FabrikOS" className="h-8 w-8 shrink-0" />
          ) : (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/logo.svg`} alt="FabrikOS" className="h-6 w-auto" />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-0.5 py-2">
          {visibleItems.map((entry) => renderLeaf(entry))}
        </nav>
      </ScrollArea>
    </aside>
  )
}
